import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { formatCNPJ, formatCPF } from '@/lib/utils/validation'
import { CustomerData, CustomerField, onlyDigits } from '@/lib/checkout/customer'

interface CustomerDataFieldsProps {
  value: CustomerData
  /** i18n keys (namespace `checkout`) per field, or the API's own message. */
  errors: Partial<Record<CustomerField, string | undefined>>
  onChange: (field: CustomerField, value: string) => void
  /** Shown when the typed CPF/CNPJ differs from the one on the account. */
  saveDocument?: { checked: boolean; onChange: (checked: boolean) => void } | null
  disabled?: boolean
  /** F-93 1B: a company pays; the name is its legal name and the document its CNPJ. */
  company?: boolean
  className?: string
}

/** `11999998888` → `(11) 99999-8888`. */
const formatPhone = (digits: string): string => {
  const d = digits.slice(0, 11)
  if (d.length <= 2) return d
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

/** One box for CPF or CNPJ: the mask follows the number of digits typed. */
const formatDocument = (digits: string): string => {
  const d = digits.slice(0, 14)
  return d.length > 11 ? formatCNPJ(d) : formatCPF(d)
}

/**
 * Who is paying: name, e-mail, CPF/CNPJ and phone.
 *
 * The payment provider refuses an order without the document, so these are
 * not optional — and they are prefilled from the account wherever the account
 * has them.
 */
export function CustomerDataFields({ value, errors, onChange, saveDocument, disabled, company = false, className }: CustomerDataFieldsProps) {
  const { t, i18n } = useTranslation('checkout')
  // An API message is shown as it came: it is already translated.
  const errorText = (error: string) => (i18n.exists(error, { ns: 'checkout' }) ? t(error) : error)

  const field = (
    name: CustomerField,
    input: ReactNode
  ) => (
    <div className="min-w-0">
      <label htmlFor={`checkout-${name}`} className="mb-1 block text-xs text-gray-500">
        {t(company && (name === 'name' || name === 'document') ? `purchase.fields.company.${name}` : `purchase.fields.${name}`)}
      </label>
      {input}
      {errors[name] && (
        <p id={`checkout-${name}-error`} className="mt-1 text-xs text-red-600">
          {errorText(errors[name] as string)}
        </p>
      )}
    </div>
  )

  const inputClass = (name: CustomerField) => cn('h-11 bg-white', errors[name] && 'border-red-500')
  const described = (name: CustomerField) => (errors[name] ? `checkout-${name}-error` : undefined)

  return (
    <div className={cn('space-y-3', className)}>
      {field(
        'name',
        <Input
          id="checkout-name"
          autoComplete="name"
          value={value.name}
          onChange={(e) => onChange('name', e.target.value)}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={described('name')}
          disabled={disabled}
          className={inputClass('name')}
        />
      )}
      {field(
        'email',
        <Input
          id="checkout-email"
          type="email"
          autoComplete="email"
          value={value.email}
          onChange={(e) => onChange('email', e.target.value)}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={described('email')}
          disabled={disabled}
          className={inputClass('email')}
        />
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {field(
          'document',
          <Input
            id="checkout-document"
            inputMode="numeric"
            value={formatDocument(onlyDigits(value.document))}
            onChange={(e) => onChange('document', onlyDigits(e.target.value).slice(0, 14))}
            placeholder={t(company ? 'purchase.placeholders.companyDocument' : 'purchase.placeholders.document')}
            aria-invalid={Boolean(errors.document)}
            aria-describedby={described('document')}
            disabled={disabled}
            className={inputClass('document')}
          />
        )}
        {field(
          'phone',
          <Input
            id="checkout-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={formatPhone(onlyDigits(value.phone))}
            onChange={(e) => onChange('phone', onlyDigits(e.target.value).slice(0, 11))}
            placeholder={t('purchase.placeholders.phone')}
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={described('phone')}
            disabled={disabled}
            className={inputClass('phone')}
          />
        )}
      </div>
      {saveDocument && (
        <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={saveDocument.checked}
            onChange={(e) => saveDocument.onChange(e.target.checked)}
            disabled={disabled}
            className="mt-0.5 h-4 w-4 flex-shrink-0"
          />
          <span>{t('purchase.saveDocument')}</span>
        </label>
      )}
    </div>
  )
}
